import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import {
  DemoRadioControl,
  DemoSliderControl,
} from '@/components/Demo';
import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';

import GridDemo from './GridDemo';
import Item from './Item';
import { GridStructure, BaseCodeWrapper } from './Grid.shared';

function PercentageVsFraction() {
  return (
    <GridDemo
      hidePerspectiveControl
      defaultPerspectiveEnabled={false}
      initialValues={{
        unit: 'fr',
        width: 100,
      }}
      customGrid={{
        gridTemplateColumns: '8rem 1fr',
      }}
      controls={[
        <DemoRadioControl
          key="unit"
          id="unit"
          label="Unit"
          options={['fr', '%']}
        />,
        <DemoSliderControl
          key="width"
          id="width"
          label="Container Width"
          min={40}
          max={100}
          handleFocusColor="white"
          previewRenderer={(val) => `${val}%`}
        />,
      ]}
    >
      {({ unit, width, isPerspectiveEnabled }) => {
        const cssCode = `\
.parent {
  display: grid;
  grid-template-columns: ${unit === 'fr' ? '1fr 3fr' : '25% 75%'};
}`;

        return (
          <Row>
            <Wrapper
              style={{
                '--width': width + '%',
              }}
            >
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <GridStructure
                  style={{
                    gridTemplateColumns:
                      unit === '%' ? '25% 75%' : '1fr 3fr',
                  }}
                >
                  <GridColumn>
                    <Ghost
                      alt=""
                      src="/images/shadow-palette/ghost.png"
                      style={{ opacity: 0 }}
                    />
                  </GridColumn>
                  <GridColumn />
                </GridStructure>
              </Layer>
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(-10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <ChildWrapper
                  style={{
                    gridTemplateColumns:
                      unit === '%' ? '25% 75%' : '1fr 3fr',
                  }}
                >
                  <GhostItem
                    layout={false}
                    style={{
                      opacity: isPerspectiveEnabled ? 0.8 : 1,
                    }}
                  >
                    <Ghost
                      alt="A cute ghost looking surprised. 3D illustration"
                      src="/images/shadow-palette/ghost.png"
                    />
                  </GhostItem>
                  <Item
                    layout={false}
                    style={{
                      opacity: isPerspectiveEnabled ? 0.8 : 1,
                    }}
                  />
                </ChildWrapper>
              </Layer>
            </Wrapper>
            <CodeWrapper>
              <ClientOnlySnippet
                omitCopyButton
                lockedToColorMode="dark"
                code={cssCode}
                lang="css"
              />
            </CodeWrapper>
          </Row>
        );
      }}
    </GridDemo>
  );
}

const Row = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  margin: 64px auto 16px;
  width: 375px;
  max-width: 100%;

  @media (max-width: 25.625rem) {
    margin-bottom: -16px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: var(--width);
  max-width: calc(100vw - 32px);
  height: 160px;
`;

const Layer = styled(motion.div)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: transform 500ms;
  will-change: transform;
`;

const ChildWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
`;

const GridColumn = styled.div`
  border: 1px solid transparent;
  transform: translateY(0.5px);
  flex: 1;
  padding: 8px 10px;

  &:not(:last-of-type) {
    border-right: 2px dashed var(--color-gray-500);
  }
`;

const Ghost = styled.img`
  position: relative;
  display: block;
  width: 55px;
  max-width: unset;
  /* prettier-ignore */
  filter:
    drop-shadow(0px 1px 4px hsl(215deg 15% 10% / 0.5))
    drop-shadow(0px 2px 16px hsl(215deg 15% 10% / 0.5));
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  width: 375px;
  max-width: 100%;
  overflow: auto;

  /* Fill the full space on mobile */
  @media (max-width: 25.625rem) {
    && {
      width: calc(100% + var(--viewport-padding) * 2);
      max-width: calc(100% + var(--viewport-padding) * 2);
      margin-inline: calc(var(--viewport-padding) * -1);
      padding-inline: 4px;
      border-radius: 0px 0px 5px 5px;
    }
  }
`;

const GhostItem = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid var(--color-gray-500);
  margin: 2px;
  padding: 8px;
  border-radius: 4px;
  background: transparent;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--color-gray-100);
  }
`;

export default PercentageVsFraction;
